/** Drawn per patient because the link carries their saved-visit reference; same look as the old static QRs. */

import { Fragment, useId, useMemo } from "react";
import * as QRCode from "qrcode";

const FROM = "#16967B";
const TO = "#71B340";
// Level H rebuilds the squares under a logo tile about a third of the width.
const ERROR_CORRECTION = "H";
const HOLE_SHARE = 0.39;

// DoktorABC symbol from the mobile app (assets/doktorabc-logo.svg), drawn in a 28x28 box.
const LOGO_PATHS = [
  "M18.8117 6.74589H20V0H7.58684V7.58684H0V20H8.77514C9.17733 20 9.56124 19.9817 9.96344 19.9452C10.2559 19.9086 10.5302 19.872 10.8044 19.8172C15.3748 18.9762 18.9945 15.3748 19.8172 10.8044H18.8117C18.3181 10.8044 17.8428 10.841 17.3675 10.9324C16.5814 14.0768 14.0768 16.5814 10.9141 17.3675C10.6216 17.4406 10.3291 17.4954 10.0366 17.5503C9.61609 17.6051 9.19561 17.6417 8.75686 17.6417H2.35832V9.96344H9.94516V2.35832H17.6234V6.80073C18.0256 6.76417 18.4095 6.74589 18.8117 6.74589Z",
  "M19.2431 8C18.841 8 18.457 8.0183 18.0548 8.05489C17.7623 8.09149 17.4881 8.12809 17.2139 8.18298C12.6435 9.0247 9.02377 12.6295 8.2011 17.204H9.20658C9.59049 17.204 9.97441 17.1674 10.3583 17.1125C10.4497 17.0942 10.5594 17.0759 10.6508 17.0576C11.4369 13.892 13.9415 11.4035 17.1042 10.6167C17.3967 10.5435 17.6892 10.4886 17.9817 10.452C18.4022 10.3971 18.8227 10.3605 19.2614 10.3605H25.66V18.0457H18.0548V25.6395H10.3766V21.193C9.97441 21.2296 9.59049 21.2479 9.1883 21.2479H8V28H20.4132V20.4062H28V8H19.2431Z",
];

const square = (x: number, y: number, n: number) => `M${x} ${y}h${n}v${n}h-${n}z`;

interface Gradient {
  id: string;
  x1: number;
  x2: number;
}

const buildQr = (value: string, idPrefix: string) => {
  const { modules } = QRCode.create(value, { errorCorrectionLevel: ERROR_CORRECTION });
  const size = modules.size;
  const eyes = [
    [0, 0],
    [size - 7, 0],
    [0, size - 7],
  ];
  const inEye = (x: number, y: number) => eyes.some(([ex, ey]) => x >= ex && x < ex + 7 && y >= ey && y < ey + 7);
  // Same parity as the size, so the cleared hole stays centred on whole squares.
  const rounded = Math.round(size * HOLE_SHARE);
  const hole = rounded % 2 === size % 2 ? rounded : rounded + 1;
  const holeStart = (size - hole) / 2;
  const inHole = (x: number, y: number) =>
    x >= holeStart && x < holeStart + hole && y >= holeStart && y < holeStart + hole;

  let data = "";
  for (let y = 0; y < size; y++) {
    for (let x = 0; x < size; x++) {
      if (modules.get(y, x) && !inEye(x, y) && !inHole(x, y)) data += square(x, y, 1);
    }
  }

  const gradients: Gradient[] = [{ id: `${idPrefix}-data`, x1: 0, x2: size }];
  const tilePad = (0.7 * hole) / 21;
  const tile = { start: holeStart + tilePad, size: hole - 2 * tilePad };
  const logo = { size: tile.size * 0.68, start: tile.start + (tile.size * 0.32) / 2 };
  const eyeShapes = eyes.map(([ex, ey], i) => {
    gradients.push({ id: `${idPrefix}-ring-${i}`, x1: ex, x2: ex + 7 }, { id: `${idPrefix}-dot-${i}`, x1: ex + 2, x2: ex + 5 });
    return {
      // Even-odd fill cuts the hole, so the ring can carry its own gradient.
      ring: `${square(ex, ey, 7)}${square(ex + 1, ey + 1, 5)}`,
      dot: square(ex + 2, ey + 2, 3),
      ringFill: `url(#${idPrefix}-ring-${i})`,
      dotFill: `url(#${idPrefix}-dot-${i})`,
    };
  });

  return { size, data, gradients, eyeShapes, holeStart, hole, tile, logo };
};

const QrCode = ({ value }: { value: string }) => {
  const idPrefix = `qr${useId().replace(/:/g, "")}`;
  const qr = useMemo(() => buildQr(value, idPrefix), [value, idPrefix]);

  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox={`0 0 ${qr.size} ${qr.size}`}
      width="100%"
      height="100%"
      shapeRendering="crispEdges"
      aria-hidden="true"
    >
      <defs>
        {qr.gradients.map((g) => (
          <linearGradient key={g.id} id={g.id} gradientUnits="userSpaceOnUse" x1={g.x1} y1="0" x2={g.x2} y2="0">
            <stop stopColor={FROM} />
            <stop offset="1" stopColor={TO} />
          </linearGradient>
        ))}
        <linearGradient id={`${idPrefix}-tile`} x1="0" y1="0" x2="1" y2="1">
          <stop stopColor={FROM} />
          <stop offset="1" stopColor={TO} />
        </linearGradient>
      </defs>
      <path fill="#FFFFFF" d={`M0 0h${qr.size}v${qr.size}H0z`} />
      <path fill={`url(#${idPrefix}-data)`} d={qr.data} />
      {qr.eyeShapes.map((eye) => (
        <Fragment key={eye.ring}>
          <path fill={eye.ringFill} fillRule="evenodd" d={eye.ring} />
          <path fill={eye.dotFill} d={eye.dot} />
        </Fragment>
      ))}
      <rect
        x={qr.holeStart}
        y={qr.holeStart}
        width={qr.hole}
        height={qr.hole}
        rx={(1.6 * qr.hole) / 21}
        fill="#FFFFFF"
      />
      <rect
        data-testid="qr-logo-tile"
        x={qr.tile.start}
        y={qr.tile.start}
        width={qr.tile.size}
        height={qr.tile.size}
        rx={qr.tile.size * 0.22}
        fill={`url(#${idPrefix}-tile)`}
        shapeRendering="geometricPrecision"
      />
      <svg
        data-testid="qr-logo"
        x={qr.logo.start}
        y={qr.logo.start}
        width={qr.logo.size}
        height={qr.logo.size}
        viewBox="0 0 28 28"
        shapeRendering="geometricPrecision"
      >
        {LOGO_PATHS.map((d) => (
          <path key={d} d={d} fill="#FFFFFF" />
        ))}
      </svg>
    </svg>
  );
};

export default QrCode;
